import { batch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import type { Action } from '@reduxjs/toolkit';

import { makeRoute, parseRoute } from './routes';
import { useRestrictConnection, useRouteState, useUpdateRoute } from './routeHooks';
import { useAppDispatch, useAppSelector } from '../configuration/setup/hooks';
import {
    getInvitationDialogPartner,
    getSelectedPartnerId,
    getServiceInfoChapter,
    getShowCreateDialog,
    getShowServiceInfoDialog,
    partnerSelected,
    SERVICE_INFO_CHAPTER_HELP,
    SERVICE_INFO_CHAPTER_WELCOME,
    serviceInfoChapter,
    showInvitationDialog,
    showServiceInfoDialog,
} from '../layout/appSlice';

const RouteUpdater = () => {
    const dispatch = useAppDispatch();
    const { search } = useLocation();
    const invitationDialogPartner = useAppSelector(getInvitationDialogPartner);

    // Parse initial route or after it has changed by browser navigation or user input
    useRouteState(() => {
        const dispatchQue: Action[] = [];

        const routeSearchParams = parseRoute(search);

        const {
            partnerId,
            showCreateDialog,
            showServiceInfoDialog: showInfoDialog,
            serviceInfoChapter: infoChapter,
        } = routeSearchParams;

        if (partnerId) {
            dispatchQue.push(partnerSelected(partnerId));
        }

        if (showCreateDialog === 'true') {
            dispatchQue.push(showInvitationDialog({ showDialog: true, partner: invitationDialogPartner }));
        } else if (showCreateDialog === 'false') {
            dispatchQue.push(showInvitationDialog({ showDialog: false }));
        }

        if (showInfoDialog === 'true') {
            dispatchQue.push(showServiceInfoDialog(true));
        } else if (showInfoDialog === 'false') {
            dispatchQue.push(showServiceInfoDialog(false));
        }

        if (infoChapter && [SERVICE_INFO_CHAPTER_WELCOME, SERVICE_INFO_CHAPTER_HELP].includes(infoChapter)) {
            dispatchQue.push(serviceInfoChapter(infoChapter));
        }

        batch(() => {
            for (const action of dispatchQue) {
                dispatch(action);
            }
        });
    });

    const showCreatePartnerDialog = useAppSelector(getShowCreateDialog);
    const showInfoDialog = useAppSelector(getShowServiceInfoDialog);
    const currentInfoChapter = useAppSelector(getServiceInfoChapter);
    const selectedPartnerId = useAppSelector(getSelectedPartnerId);
    const restrictConnectionType = useRestrictConnection();

    // Update route whenever an observed store prop changes.
    // The key of the RouteState is the search param key in the URL.
    const newRoute = makeRoute({
        partnerId: selectedPartnerId,
        showCreateDialog: showCreatePartnerDialog ? 'true' : undefined,
        showServiceInfoDialog: showInfoDialog ? 'true' : undefined,
        serviceInfoChapter: showInfoDialog ? currentInfoChapter : undefined,
        restrictConnectionType: restrictConnectionType,
    });

    useUpdateRoute(newRoute);

    return null;
};

export default RouteUpdater;
